import { SearchBox, SearchProvider } from "@elastic/react-search-ui";
import CodeSystemTabView from "components/CodeSystemTabView";
import { ElasticWrapper, Heading, InputView, Options } from "components/common";
import { useEsConfig } from "context/elasticsearch/esConfigContext";
import { CodeSystem } from "context/elasticsearch/types";
import { useStoreContext } from "context/StoreProvider";
import { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import {
  getCodeSystemEndpoint,
  getCodeSystemName,
  getCodeSystemOid
} from "utils";

import { /*isClinicalCodesystem,*/ isSmallLabCodesystem } from "../utils";
import getSearchDriverOptions from "./config/searchOptionsFactory";
import EsConfigEditor from "./EsConfigEditor";
import { SearchSectionWrapper } from "./style";

type SearchWithFilterProps = {
  codeSystem: CodeSystem;
};

const KISSearchWithFilterTestVersion = ({
  codeSystem
}: SearchWithFilterProps) => {
  const { t } = useTranslation();
  const codesystemEnum: CodeSystem = codeSystem;

  const { config } = useEsConfig(codesystemEnum);

  const { codeValue } = useParams<{ codeValue: string }>();

  const { /*state,*/ actions } = useStoreContext();
  //const { excludedSearchFields } = state
  const { updateCodeSystem, updateCodeValue, updateExcludedSearchFields } =
    actions;

  const { pathname } = useLocation();

  useEffect(() => {
    const kodeverk = getCodeSystemName(codeSystem as string);
    document.title = `${t("general.finnkode")} - ${kodeverk}`;

    // Remove any excluded search fields when switching code system
    updateExcludedSearchFields([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSystem]);

  useLayoutEffect(() => {
    updateCodeSystem(getCodeSystemEndpoint(pathname) as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* This must exist in a compoent that is parent to other components. 
			Sets the code used for almost all functionality in the app.
			Do not remove. */
  useEffect(() => {
    updateCodeValue(codeValue as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeValue]);

  // whenever `config` changes, we bump reloadKey and recompute options
  const [reloadKey, setReloadKey] = useState(0);
  const [options, setOptions] = useState(() => getSearchDriverOptions(config));

  useEffect(() => {
    setOptions(getSearchDriverOptions(config));
    setReloadKey((k) => k + 1);
  }, [config]);

  useEffect(() => {
    setOptions(getSearchDriverOptions(config));
  }, [config]);

  useEffect(() => {
    //console.log('Options updated:', options)
  }, [options]);

  return (
    <>
      {/* max-heght:90vh */}
      <div className=" p-2">
        <EsConfigEditor codeSystem={codesystemEnum} />
        {/* SearchProvider is a wrapper for the search functionality.
				It encapsulates searchTerm, results, and other elasticsearch data
				in a context that can be access by child components using the withSearch HOC.
				For an example see export statement of TabView component.
			*/}
        <SearchProvider
          key={reloadKey}
          config={{
            initialState: {
              searchTerm: "",
              resultsPerPage: isSmallLabCodesystem(pathname) ? 350 : 20
            },
            ...options
          }}
        >
          <ElasticWrapper>
            <SearchSectionWrapper id="search-wrapper" className="p-2">
              <div
                className="d-flex gap-3 justify-content-between p-0"
                id="searchOption"
              >
                <label
                  id={`${codeSystem}-search-label`}
                  htmlFor={`${codeSystem}-search-input`}
                >
                  {/* Heading above search bar */}
                  <Heading
                    variant="h1"
                    className="fs-4 fw-bold fs-md-3 mb-2"
                    id={`${codeSystem}-page-title`}
                  >
                    {t("general.searchTitle", {
                      term: getCodeSystemName(codeSystem as string)
                    })}{" "}
                    {getCodeSystemOid(codeSystem as string) !== "" && (
                      <span style={{ color: "gray", fontWeight: "normal" }}>
                        ({getCodeSystemOid(codeSystem as string)})
                      </span>
                    )}
                  </Heading>
                </label>
                <Options /> {/* Flere valg meny */}
              </div>

              {/* Search bar */}
              <SearchBox
                searchAsYouType
                autocompleteResults={false}
                autocompleteSuggestions={false}
                shouldClearFilters={false}
                debounceLength={500}
                inputView={InputView}
              />
            </SearchSectionWrapper>
            {
              <CodeSystemTabView
                codeSystem={codeSystem}
                displayScore={config.displayScore}
              />
            }
            {/* Results (everything under search bar) */}
          </ElasticWrapper>
        </SearchProvider>
      </div>
    </>
  );
};

export default KISSearchWithFilterTestVersion;
